import { ComponentType, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import clsx from 'clsx'
import BackIcon from '@ancon/wildcat-ui/shared/icons/chevron-left.svg'
import CheckIcon from '@ancon/wildcat-ui/shared/icons/check.svg'

import Button from '../../../app/components/Button'
import HeadingText from '../../../app/components/HeadingText'
import BodyText from '../../../app/components/BodyText'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import { clientContextCurrentUserCompanyNameSelector } from '../../../clientContext/store/clientContextSelectors'
import { outletsSelectedOutletListItemNameSelector } from '../../../outlets/store/outletsSelectors'
import CompanyTag from '../../../company/components/CompanyTag'
import {
  groupPreOrderCreatePendingSelector,
  preOrderNewOrderPanelSelector,
  preOrderNewOrderSelectedOrderMethodSelector,
} from '../../store/preOrderSelectors'
import {
  closeNewOrderPanel,
  setNewOrderPanelStep,
} from '../../store/preOrderSlice'
import { NewOrderPanelStep, PreOrderMethod } from '../../types'
import useNewOrderPanelSteps from '../../hooks/useNewOrderPanelSteps'
import useCompleteNewOrder from '../../hooks/useCompleteNewOrder'

import RestaurantStep from './steps/RestaurantStep'
import OrderFormatStep from './steps/OrderFormatStep'
import DateStep from './steps/DateStep'
import TimeStep from './steps/TimeStep'
import OrderTypeStep from './steps/OrderTypeStep'
import styles from './NewOrderPanel.module.scss'

const StepContent: Record<NewOrderPanelStep, ComponentType> = {
  [NewOrderPanelStep.Restaurant]: RestaurantStep,
  [NewOrderPanelStep.Format]: OrderFormatStep,
  [NewOrderPanelStep.Date]: DateStep,
  [NewOrderPanelStep.Time]: TimeStep,
  [NewOrderPanelStep.OrderType]: OrderTypeStep,
}

/**
 * New company order: one step at a time down a rail. Done steps show
 * what was chosen and can be opened again; later steps wait their turn.
 */
type NewOrderPanelProps = {
  className?: string
}

export default function NewOrderPanel({ className }: NewOrderPanelProps) {
  const { t } = useTranslation('preOrder')
  const dispatch = useAppDispatch()

  const { currentStep } = useAppSelector(preOrderNewOrderPanelSelector)
  const companyName = useAppSelector(
    clientContextCurrentUserCompanyNameSelector,
  )
  const outletName = useAppSelector(outletsSelectedOutletListItemNameSelector)
  const orderMethod = useAppSelector(
    preOrderNewOrderSelectedOrderMethodSelector,
  )
  const isCreatingGroupOrder = useAppSelector(
    groupPreOrderCreatePendingSelector,
  )

  const steps = useNewOrderPanelSteps()
  const completeNewOrder = useCompleteNewOrder()

  const [isAttentionShown, setIsAttentionShown] = useState(false)

  const currentIndex = steps.findIndex(({ step }) => step === currentStep)
  const isLastStep = currentIndex === steps.length - 1

  function getNextButtonLabel() {
    if (!isLastStep) {
      return t('newOrderPanel.next', {
        step: t(`newOrderPanel.shortSteps.${steps[currentIndex + 1].step}`),
      })
    }

    return orderMethod === PreOrderMethod.Group
      ? t('newOrderPanel.createGroupOrder')
      : t('newOrderPanel.continueToMenu')
  }

  function handleOpenStep(step: NewOrderPanelStep) {
    setIsAttentionShown(false)
    dispatch(setNewOrderPanelStep(step))
  }

  function handleNext() {
    if (!steps[currentIndex]?.isComplete) {
      setIsAttentionShown(true)
      return
    }

    setIsAttentionShown(false)

    // Go back to the first step that still needs a choice, e.g. after an edit
    const nextIncomplete = steps.find(
      ({ isComplete }, index) => index > currentIndex && !isComplete,
    )
    const firstIncomplete = steps.find(({ isComplete }) => !isComplete)

    if (!isLastStep) {
      dispatch(
        setNewOrderPanelStep((nextIncomplete ?? steps[currentIndex + 1]).step),
      )
    } else if (firstIncomplete) {
      dispatch(setNewOrderPanelStep(firstIncomplete.step))
    } else {
      completeNewOrder()
    }
  }

  return (
    <section
      className={clsx(styles.panel, className)}
      aria-labelledby="new-order-panel-title"
    >
      <div className={styles.header}>
        <Button
          variant="secondary"
          className={styles.closeButton}
          onClick={() => dispatch(closeNewOrderPanel())}
          aria-label={t('newOrderPanel.close')}
        >
          <BackIcon />
        </Button>
        <div className={styles.title}>
          <HeadingText as="h2" size="h3" id="new-order-panel-title">
            {t('newOrderPanel.title')}
          </HeadingText>
          <BodyText as="span" color="body-2" fontSize="1.2rem">
            {t('newOrderPanel.forCompany', { companyName })}
            {outletName &&
              ` · ${t('newOrderPanel.fromRestaurant', { restaurant: outletName })}`}
          </BodyText>
        </div>
        <CompanyTag>{t('newOrderPanel.companyOrder')}</CompanyTag>
      </div>

      <ol className={styles.steps}>
        {steps.map(({ step, value, isComplete }, index) => {
          const isCurrent = index === currentIndex
          const isDone = index < currentIndex && isComplete
          const isLocked = index > currentIndex
          const Content = StepContent[step]

          return (
            <li
              key={step}
              className={clsx(styles.step, {
                [styles.done]: isDone,
                [styles.current]: isCurrent,
              })}
              aria-current={isCurrent ? 'step' : undefined}
            >
              <div className={styles.rail} aria-hidden>
                <span className={styles.dot}>
                  {isDone ? <CheckIcon /> : index + 1}
                </span>
                <span className={styles.line} />
              </div>
              <div className={styles.body}>
                <button
                  type="button"
                  className={styles.stepHeader}
                  onClick={() => handleOpenStep(step)}
                  disabled={isLocked || isCurrent}
                >
                  <span className={styles.stepText}>
                    <span className={styles.stepLabel}>
                      {t(`newOrderPanel.steps.${step}`)}
                    </span>
                    <span
                      className={clsx(styles.stepValue, {
                        [styles.placeholder]: !value,
                      })}
                    >
                      {value ?? t(`newOrderPanel.placeholders.${step}`)}
                    </span>
                  </span>
                  {isDone && (
                    <span className={styles.edit}>
                      {t('newOrderPanel.edit')}
                    </span>
                  )}
                </button>
                {isCurrent && (
                  <div className={styles.content}>
                    <Content />
                    {isAttentionShown && !isComplete && (
                      <BodyText
                        as="span"
                        className={styles.attention}
                        role="alert"
                      >
                        {t('newOrderPanel.makeAChoice')}
                      </BodyText>
                    )}
                    <Button
                      size="large"
                      className={styles.nextButton}
                      onClick={handleNext}
                      loading={isLastStep && isCreatingGroupOrder}
                      disabled={!isComplete && isAttentionShown}
                    >
                      {getNextButtonLabel()}
                    </Button>
                  </div>
                )}
              </div>
            </li>
          )
        })}
      </ol>
    </section>
  )
}
